import { useEffect, useState, type ReactNode } from 'react';
import { Bot, ExternalLink } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  TYPES_ECHEANCE,
  useEnregistrerEcheance,
  useSupprimerEcheance,
  type LigneEcheance,
  type TypeEcheance,
} from '../../lib/useVeille';
import { CLASSE_CHAMP, CLASSE_LIBELLE, CLASSE_ZONE_TEXTE, instantLisible, jourLisible } from './format';

/**
 * Saisie, correction et retrait d'une échéance suivie (CDC §6.11). Une fiche
 * RNCP ou RS se désigne par son numéro : sa date est relevée chaque jour à
 * France Compétences. Les agréments, la certification qualité et les
 * habilitations n'ont pas de source ouverte : leur date se saisit. Une
 * échéance tenue par le robot se lit ici sans se corriger — la raison vient
 * de l'API.
 */

const NATURES_PROPOSEES = [
  'Renouvellement',
  "Fin d'enregistrement",
  'Audit de surveillance',
  'Audit de renouvellement',
  'Fin de validité',
];

function estFiche(type: TypeEcheance): boolean {
  return type === 'RNCP' || type === 'RS';
}

interface Formulaire {
  type: TypeEcheance;
  intitule: string;
  reference: string;
  nature: string;
  dateEcheance: string;
  autorite: string;
  notes: string;
  siteCode: string;
}

const VIDE: Formulaire = {
  type: 'AGREMENT',
  intitule: '',
  reference: '',
  nature: 'Renouvellement',
  dateEcheance: '',
  autorite: '',
  notes: '',
  siteCode: '',
};

function Detail({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">{libelle}</dt>
      <dd className="mt-0.5 break-words text-sm text-codialis-navy">{children}</dd>
    </div>
  );
}

export function ModaleEcheance({
  ouverte,
  onFermer,
  echeance,
  sites,
  preavisMois,
}: {
  ouverte: boolean;
  onFermer: () => void;
  /** `null` : nouvelle échéance. */
  echeance: LigneEcheance | null;
  sites: string[];
  preavisMois: number;
}): JSX.Element | null {
  const notifier = useToast();
  const enregistrer = useEnregistrerEcheance();
  const supprimer = useSupprimerEcheance();
  const [formulaire, setFormulaire] = useState<Formulaire>(VIDE);
  const [confirmerRetrait, setConfirmerRetrait] = useState(false);

  useEffect(() => {
    if (!ouverte) return;
    setFormulaire(
      echeance
        ? {
            type: echeance.type,
            intitule: echeance.intitule,
            reference: echeance.reference ?? '',
            nature: echeance.nature,
            dateEcheance: echeance.dateEcheance ?? '',
            autorite: echeance.autorite ?? '',
            notes: echeance.notes ?? '',
            siteCode: echeance.siteCode ?? '',
          }
        : VIDE,
    );
    setConfirmerRetrait(false);
    enregistrer.reset();
    supprimer.reset();
    // Réinitialisée à chaque ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte, echeance?.id]);

  const changer = <C extends keyof Formulaire>(champ: C, valeur: Formulaire[C]): void =>
    setFormulaire((avant) => ({ ...avant, [champ]: valeur }));

  function valider(): void {
    enregistrer.mutate(
      {
        id: echeance?.id ?? null,
        saisie: {
          type: formulaire.type,
          intitule: formulaire.intitule,
          reference: formulaire.reference || null,
          nature: formulaire.nature,
          dateEcheance: formulaire.dateEcheance || null,
          autorite: formulaire.autorite || null,
          notes: formulaire.notes || null,
          siteCode: formulaire.siteCode || null,
        },
      },
      {
        onSuccess: (ligne) => {
          notifier(echeance ? 'Échéance mise à jour' : `Échéance suivie · ${ligne.etatLibelle}`);
          onFermer();
        },
      },
    );
  }

  function retirer(): void {
    if (!echeance) return;
    supprimer.mutate(echeance.id, {
      onSuccess: () => {
        notifier('Échéance retirée de la veille');
        onFermer();
      },
    });
  }

  const lectureSeule = echeance !== null && echeance.refusModification !== null;
  const fiche = estFiche(formulaire.type);
  const erreur = enregistrer.error ?? supprimer.error;

  return (
    <Modale
      titre={echeance ? echeance.intitule : 'Suivre une échéance'}
      sousTitre={
        echeance
          ? `${echeance.typeLibelle}${echeance.referenceLisible ? ` · ${echeance.referenceLisible}` : ''}`
          : `L'alerte part ${preavisMois} mois avant l'échéance, au pôle pédagogique et à la direction`
      }
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {echeance && (
          <div className="flex flex-wrap items-center gap-2">
            <Pastille texte={echeance.etatLibelle} ton={echeance.etatTon} />
            {echeance.dateAlerte && (
              <span className="text-xs text-codialis-gray">Alerte le {jourLisible(echeance.dateAlerte)}</span>
            )}
          </div>
        )}

        {lectureSeule && echeance ? (
          <>
            <div className="flex items-start gap-2 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-gray">
              <Bot className="mt-0.5 h-4 w-4 shrink-0 text-codialis-navy" aria-hidden="true" />
              <span>{echeance.refusModification}</span>
            </div>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Detail libelle="Échéance">
                {echeance.nature} · {echeance.dateEcheance ? jourLisible(echeance.dateEcheance) : 'à relever'}
              </Detail>
              <Detail libelle="Autorité">{echeance.autorite ?? '—'}</Detail>
              <Detail libelle="Formations concernées">
                {echeance.formations.length > 0
                  ? echeance.formations.map((formation) => formation.titre).join(', ')
                  : 'Aucune formation active'}
              </Detail>
              <Detail libelle="Vérifiée à la source">
                {echeance.verifieeLe ? instantLisible(echeance.verifieeLe) : 'Pas encore'}
              </Detail>
            </dl>
            {echeance.lien && (
              <a
                href={echeance.lien}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-codialis-sky hover:underline"
              >
                Voir la fiche à France Compétences
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            )}
            <div className="flex justify-end">
              <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>Type</span>
                <select
                  value={formulaire.type}
                  onChange={(evenement) => {
                    const type = evenement.target.value as TypeEcheance;
                    setFormulaire((avant) => ({
                      ...avant,
                      type,
                      nature: estFiche(type) && !avant.nature ? "Fin d'enregistrement" : avant.nature,
                    }));
                  }}
                  className={CLASSE_CHAMP}
                >
                  {TYPES_ECHEANCE.map((type) => (
                    <option key={type.valeur} value={type.valeur}>
                      {type.libelle}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>
                  {fiche ? `Numéro de fiche ${formulaire.type}` : 'Référence (facultatif)'}
                </span>
                <input
                  value={formulaire.reference}
                  onChange={(evenement) => changer('reference', evenement.target.value)}
                  placeholder={fiche ? (formulaire.type === 'RS' ? 'RS5616' : 'RNCP37098') : "N° d'agrément, de certificat…"}
                  className={CLASSE_CHAMP}
                />
              </label>
            </div>

            <label className="block min-w-0">
              <span className={CLASSE_LIBELLE}>Intitulé</span>
              <input
                value={formulaire.intitule}
                onChange={(evenement) => changer('intitule', evenement.target.value)}
                placeholder="Certification Qualiopi, agrément CFA…"
                className={CLASSE_CHAMP}
              />
            </label>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>Ce qui échoit</span>
                <input
                  value={formulaire.nature}
                  onChange={(evenement) => changer('nature', evenement.target.value)}
                  list="natures-echeance"
                  className={CLASSE_CHAMP}
                />
                <datalist id="natures-echeance">
                  {NATURES_PROPOSEES.map((nature) => (
                    <option key={nature} value={nature} />
                  ))}
                </datalist>
              </label>
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>
                  Date d’échéance{fiche ? ' (facultatif)' : ''}
                </span>
                <input
                  type="date"
                  value={formulaire.dateEcheance}
                  onChange={(evenement) => changer('dateEcheance', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </label>
            </div>
            {fiche && (
              <p className="-mt-1 text-[11px] text-codialis-gray">
                La date d’une fiche RNCP ou RS est relevée chaque jour à France Compétences et remplace
                celle saisie.
              </p>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>Autorité (facultatif)</span>
                <input
                  value={formulaire.autorite}
                  onChange={(evenement) => changer('autorite', evenement.target.value)}
                  placeholder="Certificateur, Région, DREETS…"
                  className={CLASSE_CHAMP}
                />
              </label>
              <label className="block min-w-0">
                <span className={CLASSE_LIBELLE}>Antenne</span>
                <select
                  value={formulaire.siteCode}
                  onChange={(evenement) => changer('siteCode', evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  <option value="">Tout l’organisme</option>
                  {sites.map((site) => (
                    <option key={site} value={site}>
                      Antenne {site}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block min-w-0">
              <span className={CLASSE_LIBELLE}>Notes (facultatif)</span>
              <textarea
                value={formulaire.notes}
                onChange={(evenement) => changer('notes', evenement.target.value)}
                rows={3}
                className={CLASSE_ZONE_TEXTE}
              />
            </label>

            {echeance?.modifiePar && (
              <p className="text-[11px] text-codialis-gray">
                Modifiée par {echeance.modifiePar} le {instantLisible(echeance.modifieLe)}
              </p>
            )}

            {erreur instanceof ErreurApi && <MessageErreur message={erreur.message} />}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {echeance && echeance.refusSuppression === null && (
                  confirmerRetrait ? (
                    <button
                      type="button"
                      onClick={retirer}
                      disabled={supprimer.isPending}
                      className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60 sm:w-auto"
                    >
                      {supprimer.isPending ? 'Retrait…' : 'Confirmer le retrait'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmerRetrait(true)}
                      className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-red-200 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-50 sm:w-auto"
                    >
                      Retirer de la veille
                    </button>
                  )
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
                <BoutonPrincipal onClick={valider} disabled={enregistrer.isPending}>
                  {enregistrer.isPending ? 'Enregistrement…' : echeance ? 'Enregistrer' : 'Suivre l’échéance'}
                </BoutonPrincipal>
              </div>
            </div>
          </>
        )}
      </div>
    </Modale>
  );
}
